"use client"

import type React from "react"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Eye, EyeOff, Mail, Lock, User, Phone, MapPin } from "lucide-react"
import { authAPI, SignupCredentials } from "@/lib/api"
import { useRouter } from "next/navigation"

export function SignupForm() {
    const router = useRouter()
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState("")
    
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        mobile_number: "",
        password: "",
        confirmPassword: "",
        country: "",
    })

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }))
        if (error) setError("")
    }

    const validateForm = () => {
        if (!formData.name.trim()) {
            setError("Please enter your full name")
            return false
        }
        if (!formData.email.trim()) {
            setError("Please enter your email address")
            return false
        }
        if (!formData.mobile_number.trim()) {
            setError("Please enter your mobile number")
            return false
        }
        if (!formData.password.trim()) {
            setError("Please enter a password")
            return false
        }
        if (formData.password.length < 6) {
            setError("Password must be at least 6 characters long")
            return false
        }
        if (formData.password !== formData.confirmPassword) {
            setError("Passwords do not match")
            return false
        }
        if (!formData.country) {
            setError("Please select your country")
            return false
        }
        return true
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!validateForm()) {
            return
        }

        setIsLoading(true)
        setError("")

        try {
            const signupData: SignupCredentials = {
                name: formData.name.trim(),
                email: formData.email.trim(),
                mobile_number: formData.mobile_number.trim(),
                password: formData.password,
                confirm_password: formData.confirmPassword,
                country: formData.country,
            }

            console.log('Signup data being sent:', signupData)
            const response = await authAPI.signUp(signupData)
            console.log('Signup response:', response)

            if (response.success) {
                console.log("Signup successful:", response.message)
                // Redirect to login page after successful signup
                router.push("/login")
            } else {
                setError(response.message || "Signup failed")
            }
        } catch (error) {
            console.error("Signup error:", error)
            setError("An unexpected error occurred. Please try again.")
        } finally {
            setIsLoading(false)
        }
    }

    const countries = [
        "United Arab Emirates", "Saudi Arabia", "Qatar", "Kuwait", "Bahrain", "Oman",
        "India", "Pakistan", "Bangladesh", "Sri Lanka", "Nepal", "Philippines",
        "United States", "United Kingdom", "Canada", "Australia", "Germany", "France",
        "Italy", "Spain", "Netherlands", "Switzerland", "Sweden", "Norway",
        "Other"
    ]

    return (
        <Card className="border-border shadow-lg">
            <CardHeader className="space-y-1 pb-4">
                <div className="text-center">
                    <h2 className="text-xl font-semibold text-card-foreground">Create Account</h2>
                    <p className="text-sm text-muted-foreground mt-1">Fill in your details to get started</p>
                </div>
            </CardHeader>
            <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="name" className="text-sm font-medium text-foreground">
                            Full Name
                        </Label>
                        <div className="relative">
                            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                            <Input
                                id="name"
                                type="text"
                                placeholder="Enter your full name"
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                className="pl-10 bg-input border-border focus:ring-ring"
                                required
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="email" className="text-sm font-medium text-foreground">
                            Email Address
                        </Label>
                        <div className="relative">
                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                            <Input
                                id="email"
                                type="email"
                                placeholder="Enter your email"
                                value={formData.email}
                                onChange={(e) => handleInputChange('email', e.target.value)}
                                className="pl-10 bg-input border-border focus:ring-ring"
                                required
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="mobile_number" className="text-sm font-medium text-foreground">
                            Mobile Number
                        </Label>
                        <div className="relative">
                            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                            <Input
                                id="mobile_number"
                                type="tel"
                                placeholder="Enter your mobile number"
                                value={formData.mobile_number}
                                onChange={(e) => handleInputChange('mobile_number', e.target.value)}
                                className="pl-10 bg-input border-border focus:ring-ring"
                                required
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="country" className="text-sm font-medium text-foreground">
                            Country
                        </Label>
                        <div className="relative">
                            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4 z-10" />
                            <Select value={formData.country} onValueChange={(value) => handleInputChange('country', value)}>
                                <SelectTrigger className="pl-10 bg-input border-border focus:ring-ring">
                                    <SelectValue placeholder="Select your country" />
                                </SelectTrigger>
                                <SelectContent>
                                    {countries.map((country) => (
                                        <SelectItem key={country} value={country}>
                                            {country}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="password" className="text-sm font-medium text-foreground">
                            Password
                        </Label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                            <Input
                                id="password"
                                type={showPassword ? "text" : "password"}
                                placeholder="Create a password"
                                value={formData.password}
                                onChange={(e) => handleInputChange('password', e.target.value)}
                                className="pl-10 pr-10 bg-input border-border focus:ring-ring"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            >
                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="confirmPassword" className="text-sm font-medium text-foreground">
                            Confirm Password
                        </Label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                            <Input
                                id="confirmPassword"
                                type={showConfirmPassword ? "text" : "password"}
                                placeholder="Confirm your password"
                                value={formData.confirmPassword}
                                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                                className="pl-10 pr-10 bg-input border-border focus:ring-ring"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            >
                                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>

                    {error && (
                        <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
                            {error}
                        </div>
                    )}

                    <div className="flex items-center space-x-2">
                        <input
                            id="terms"
                            type="checkbox"
                            className="w-4 h-4 text-primary bg-input border-border rounded focus:ring-ring"
                            required
                        />
                        <Label htmlFor="terms" className="text-sm text-muted-foreground">
                            I agree to the{" "}
                            <a href="/terms" className="text-blue hover:underline font-medium">
                                Terms of Service
                            </a>{" "}
                            and{" "}
                            <a href="/privacy" className="text-blue hover:underline font-medium">
                                Privacy Policy
                            </a>
                        </Label>
                    </div>

                    <Button
                        type="submit"
                        className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <div className="flex items-center space-x-2">
                                <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                                <span>Creating account...</span>
                            </div>
                        ) : (
                            "Create Account"
                        )}
                    </Button>
                </form>

                <div className="mt-6">
                    <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-border" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                            <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
                        </div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3">
                        <Button
                            variant="outline"
                            className="border-border hover:bg-muted bg-transparent"
                            onClick={() => console.log("Google signup")}
                        >
                            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                                <path
                                    fill="currentColor"
                                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                />
                                <path
                                    fill="currentColor"
                                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                />
                                <path
                                    fill="currentColor"
                                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                />
                                <path
                                    fill="currentColor"
                                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                />
                            </svg>
                            Google
                        </Button>
                        <Button
                            variant="outline"
                            className="border-border hover:bg-muted bg-transparent"
                            onClick={() => console.log("Apple signup")}
                        >
                            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12.017 0C8.396 0 8.025.044 6.79.207 5.56.37 4.703.644 3.967 1.007c-.74.364-1.379.85-2.009 1.48C1.329 3.117.843 3.756.48 4.496.116 5.231-.158 6.089-.32 7.318-.484 8.553-.528 8.924-.528 12.545s.044 3.992.207 5.227c.163 1.229.437 2.087.8 2.822.364.74.85 1.379 1.48 2.009.63.63 1.269 1.116 2.009 1.48.735.364 1.593.638 2.822.8 1.235.164 1.606.208 5.227.208s3.992-.044 5.227-.207c1.229-.163 2.087-.437 2.822-.8.74-.365 1.379-.85 2.009-1.48.63-.63 1.116-1.269 1.48-2.009.364-.735.638-1.593.8-2.822.164-1.235.208-1.606.208-5.227s-.044-3.992-.207-5.227c-.163-1.229-.437-2.087-.8-2.822-.365-.74-.85-1.379-1.48-2.009C20.883 1.329 20.244.843 19.504.48 18.769.116 17.911-.158 16.682-.32 15.447-.484 15.076-.528 11.455-.528s-3.992.044-5.227.207c-1.229.163-2.087.437-2.822.8-.74.365-1.379.85-2.009 1.48C.767 3.117.281 3.756-.082 4.496c-.364.735-.638 1.593-.8 2.822C-.984 8.553-1.028 8.924-1.028 12.545s.044 3.992.207 5.227c.163 1.229.437 2.087.8 2.822.365.74.85 1.379 1.48 2.009.63.63 1.269 1.116 2.009 1.48.735.364 1.593.638 2.822.8 1.235.164 1.606.208 5.227.208s3.992-.044 5.227-.207c1.229-.163 2.087-.437 2.822-.8.74-.365 1.379-.85 2.009-1.48.63-.63 1.116-1.269 1.48-2.009.364-.735.638-1.593.8-2.822.164-1.235.208-1.606.208-5.227s-.044-3.992-.207-5.227c-.163-1.229-.437-2.087-.8-2.822C23.172.843 22.533.329 21.793-.034 21.058-.398 20.2-.672 18.971-.835 17.736-.999 17.365-1.043 13.744-1.043s-3.992.044-5.227.207c-1.229.163-2.087.437-2.822.8-.74.365-1.379.85-2.009 1.48C2.056 3.117 1.57 3.756 1.207 4.496.843 5.231.569 6.089.406 7.318.242 8.553.198 8.924.198 12.545s.044 3.992.207 5.227c.163 1.229.437 2.087.8 2.822.365.74.85 1.379 1.48 2.009.63.63 1.269 1.116 2.009 1.48.735.364 1.593.638 2.822.8 1.235.164 1.606.208 5.227.208s3.992-.044 5.227-.207c1.229-.163 2.087-.437 2.822-.8.74-.365 1.379-.85 2.009-1.48.63-.63 1.116-1.269 1.48-2.009.364-.735.638-1.593.8-2.822.164-1.235.208-1.606.208-5.227s-.044-3.992-.207-5.227c-.163-1.229-.437-2.087-.8-2.822C23.172.843 22.533.329 21.793-.034 21.058-.398 20.2-.672 18.971-.835 17.736-.999 17.365-1.043 13.744-1.043zm0 5.691c-3.79 0-6.864 3.073-6.864 6.864s3.073 6.864 6.864 6.864 6.864-3.073 6.864-6.864-3.073-6.864-6.864-6.864zm0 11.32c-2.459 0-4.455-1.996-4.455-4.455s1.996-4.455 4.455-4.455 4.455 1.996 4.455 4.455-1.996 4.455-4.455 4.455zm8.749-11.616c0 .884-.716 1.6-1.6 1.6s-1.6-.716-1.6-1.6.716-1.6 1.6-1.6 1.6.716 1.6 1.6z" />
                            </svg>
                            Apple
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    )
}